import { Component, Input, OnInit, TemplateRef, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { FormControlPipe } from "@bequeathed/ngx-utils";
import { BaseControl } from '../base-control/base-control.component';
import { LoggerService } from "@bequeathed/ngx-app-core";
import { NgClass } from "@angular/common";
import { InterviewState, Question } from "@bequeathed/ngx-services";

const LOG: LoggerService = LoggerService.get('BaseInputControl');

/**
 * Root of all custom form input controls providing shared functionality
 *
 * @author Dan Bennett (dbennett)
 */
@Component({
    standalone: true,
    selector: 'base-input-control',
    templateUrl: './base-input-control.component.html',
    styleUrls: ['./base-input-control.component.sass', '../../base-form/base-form.component.sass'],
    imports: [
        // Angular
        NgClass,
        ReactiveFormsModule,
        // Bequeathed
        FormControlPipe
    ],
    encapsulation: ViewEncapsulation.None
})
export class BaseInputControl extends BaseControl implements OnInit {

    static nextUID = 0;
    uid = BaseInputControl.nextUID++;

    // keeps up-to-date input validation state
    @Input() wrappingClass = 'form-group position-relative';
    @Input() inputClass = 'form-control';

    inputType = 'text';

    override ngOnInit(): void {
        if (!this.form) {
            this.form = this.baseForm.form;
        }
        super.ngOnInit();

        // @ts-expect-error this is fine
        this.control['ctrl'] = this;
    }

    /**
     * Shortcut for revalidateControl used in the template
     * @param form A formgroup instance representing main or sub-form on the current page
     * (base-form-component). By default it uses main FormGroup from the base-form instance
     */
    async revalidateInput(form: UntypedFormGroup = this.form): Promise<void> {
        this.baseForm.revalidateControl(this.question.id!, form);
    }

    markAsTouched(): void {
        this.control.markAsTouched();
        LOG.debug('isTouched', `${this.control.touched}`);
    }

    /**
     * Checks if an object is TemplateRef, a more complex object than just string to display for the input field
     * @param obj
     */
    isElementRef(obj: TemplateRef<any> | string): boolean {
        return obj instanceof TemplateRef;
    }

    convertWhitespaces(text: string): string {
        return text.replace ? text.replace(/\s/g, '+').replace(/'/g, '*') : text;
    }

    getAriaLabels(additionalKey: string | null = null) {
        let labels = '';

        if (this.controlHasError()) {
            labels += `${this.question.id}-err `;
        }

        if (additionalKey) {
            labels += `${this.question.id}-${this.convertWhitespaces(additionalKey)} `;
        }

        return labels.length > 0 ? labels.trim() : null;
    }

    override applyValue(state: InterviewState | Question): void {
        if (state instanceof InterviewState) {
            state.currentQuestion!.userResponse = this.control.value;
        } else {
            state.userResponse = this.control.value;
        }
    }

    override trim(): void {
        if (['currency', 'number', 'percentage'].reduce((acc: boolean, val: string) => val !== this.inputType && acc, false)) {
            if (this.control.value) {
                this.control.setValue(this.control.value.trim());
                this.modelValue = this.modelValue.trim();
            } else {
                this.control.setValue(null);
                this.modelValue = null;
            }
        }
    }

}


